import { Host, forceUpdate, h } from "@stencil/core";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
import { getControlRegisterProperty } from "../../common/registry-properties";
import { COMBO_BOX_HOST_PARTS, COMBO_BOX_PARTS_DICTIONARY } from "../../common/reserved-names";
import { isMobileDevice, tokenMap } from "../../common/utils";
import { focusComposedPath } from "../common/helpers";
import { filterSubModel } from "./helpers";
import { computeComboBoxItemImage, getComboBoxImages } from "./item-images";
import { findNextSelectedIndex, findSelectedIndex } from "./navigation";
import { customComboBoxItemRender, nativeItemRender } from "./renders";
import { comboBoxActiveDescendantIsRendered, findComboBoxLargestValue, getComboBoxItemFromMouseEvent, mapValuesToItemInfo, popoverWasClicked } from "./utils";
const SELECTED_ITEM_SELECTOR = `button[part*='${COMBO_BOX_PARTS_DICTIONARY.SELECTED}']`;
const mobileDevice = isMobileDevice();
const DEFAULT_GET_IMAGE_PATH_CALLBACK = (itemUIModel, iconDirection) => ({
    base: iconDirection === "start" ? itemUIModel.startImgSrc : itemUIModel.endImgSrc
});
/**
 * @status experimental
 *
 * The ch-combo-box-render is an input widget that has an associated popup. The
 * popup enables users to choose a value for the input from a collection.
 *  - Items are only rendered when the popup is displayed.
 *
 * @part ... - ...
 */
export class ChComboBoxRender {
    constructor() {
        this.activeDescendant = undefined;
        this.expanded = false;
        this.accessibleName = undefined;
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.hostParts = undefined;
        this.model = [];
        this.multiple = false;
        this.name = undefined;
        this.placeholder = undefined;
        this.popoverInlineAlign = "inside-start";
        this.readonly = false;
        this.resizable = false;
        this.suggest = false;
        this.suggestDebounce = 250;
        this.suggestOptions = {};
        this.value = undefined;
    }
    #accessibleNameFromExternalLabel;
    /**
     * This variable is used to emulate the behavior of the native select. The
     * native select decides its min size based on the size of the largest option.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #largestValue;
    /**
     * Last value that was set correctly in the control. Useful to revert the
     * value when applying strict filters.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #lastConfirmedValue;
    #valueToItemInfo = new Map();
    #captionToItemInfo = new Map();
    #itemImages;
    #shouldFocusTheComboBox = false;
    // Filters info
    #applyFilters = false;
    #queuedInputValueUpdate;
    /**
     * Collection of displayed values. If a filter is applied and the value
     * belongs to this Set, the item is displayed.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #displayedValues; // Don't allocate memory until needed
    #selectNextIndex = (event, currentSelectedIndex, increment, hasFilters, displayedValues) => {
        event.preventDefault(); // Stop ArrowDown key from scrolling
        const nextSelectedIndex = currentSelectedIndex.type === "not-exists"
            ? findNextSelectedIndex(this.model, {
                type: "first-level",
                firstLevelIndex: increment === 1 ? -1 : this.model.length
            }, increment, hasFilters, displayedValues)
            : findNextSelectedIndex(this.model, currentSelectedIndex, increment, hasFilters, displayedValues);
        if (nextSelectedIndex.type === "not-exists") {
            return;
        }
        // The new selected value is either in the first level or in the group
        const newSelectedValue = nextSelectedIndex.type === "first-level"
            ? this.model[nextSelectedIndex.firstLevelIndex]
            : this.model[nextSelectedIndex.firstLevelIndex]
                .items[nextSelectedIndex.secondLevelIndex];
        if (this.activeDescendant !== newSelectedValue) {
            this.activeDescendant = newSelectedValue;
        }
    };
    // Keyboard events when the control has no filters
    #keyEventsNoFiltersDictionary = {
        ArrowUp: (event) => {
            if (this.expanded) {
                this.#selectNextIndex(event, findSelectedIndex(this.#valueToItemInfo, this.activeDescendant), -1, this.suggest && !this.#isModelAlreadyFiltered(), this.#displayedValues);
            }
            // Open the combo-box, without selecting any value
            else {
                event.preventDefault(); // Stop space key from scrolling
                this.#shouldFocusTheComboBox = true;
                this.expanded = true;
            }
        },
        ArrowDown: (event) => {
            if (this.expanded) {
                this.#selectNextIndex(event, findSelectedIndex(this.#valueToItemInfo, this.activeDescendant), 1, this.suggest && !this.#isModelAlreadyFiltered(), this.#displayedValues);
            }
            // Open the combo-box, without selecting any value
            else {
                event.preventDefault(); // Stop space key from scrolling
                this.#shouldFocusTheComboBox = true;
                this.expanded = true;
            }
        },
        Home: (event) => this.#selectNextIndex(event, {
            type: "first-level",
            firstLevelIndex: -1
        }, // The algorithm will sum 1 to the start index
        1, this.suggest && !this.#isModelAlreadyFiltered(), this.#displayedValues),
        End: (event) => this.#selectNextIndex(event, {
            type: "first-level",
            firstLevelIndex: this.model.length
        }, // The algorithm will sum -1 to the start index
        -1, this.suggest && !this.#isModelAlreadyFiltered(), this.#displayedValues),
        Enter: () => {
            // The focus must return to the Host when closing the popover
            if (this.expanded) {
                this.#shouldFocusTheComboBox = true;
            }
            this.expanded = !this.expanded;
        },
        // Same as the Enter handler
        NumpadEnter: () => {
            // The focus must return to the Host when closing the popover
            if (this.expanded) {
                this.#shouldFocusTheComboBox = true;
            }
            this.expanded = !this.expanded;
        },
        Space: event => {
            event.preventDefault(); // Stop space key from scrolling
            // Only expands the ComboBox
            this.expanded ||= true;
        },
        Tab: event => {
            // The focus must return to the Host when tabbing with the popover
            // expanded
            if (this.expanded) {
                event.preventDefault();
                this.#shouldFocusTheComboBox = true;
                this.expanded = false;
            }
        }
    };
    // Keyboard events when the control has filters
    #keyEventsWithFiltersDictionary = {
        ArrowUp: (event) => this.#keyEventsNoFiltersDictionary.ArrowUp(event),
        ArrowDown: (event) => this.#keyEventsNoFiltersDictionary.ArrowDown(event),
        Enter: (event) => this.#checkAndEmitValueChangeWithFilters(event),
        // Same as the Enter handler
        NumpadEnter: (event) => this.#checkAndEmitValueChangeWithFilters(event),
        Tab: (event) => this.#checkAndEmitValueChangeWithFilters(event)
    };
    // Refs
    // #maskRef: HTMLDivElement;
    #inputRef;
    #selectRef;
    handleExpandedChange(newExpandedValue) {
        if (newExpandedValue && !mobileDevice) {
            this.#setComboBoxIcons();
            // Sync the active descendant when expanding the combo-box
            this.#syncActiveDescendant();
            // TODO: Add a unit test for this case
            // When the control is expanded and has filters applied, we should
            // refresh the rendered items without any debounce
            if (this.suggest) {
                this.#scheduleFilterProcessing();
            }
        }
        // Free the memory, since the combo-box does not won't render any images
        else {
            this.#itemImages = undefined;
        }
    }
    internals;
    el;
    modelChanged(newModel) {
        this.#findLargestValue(this.model);
        mapValuesToItemInfo(newModel, this.#valueToItemInfo, this.#captionToItemInfo);
        // TODO: Add a unit test for this
        // The model can change when the combo-box is expanded by having server
        // filters. In this case, we need to re-compute the icons
        if (this.expanded) {
            this.#setComboBoxIcons();
        }
        // this.#checkIfCurrentSelectedValueIsNoLongerValid();
        // This must be the last operation, since it needs to wait for the UI Model
        // Map to be updated (#valueToItemInfo)
        this.#setValueInForm(this.value);
    }
    suggestChanged() {
        this.#scheduleFilterProcessing();
    }
    suggestDebounceChanged() {
        if (this.suggest) {
            this.#scheduleFilterProcessing();
        }
    }
    suggestOptionsChanged() {
        this.#scheduleFilterProcessing();
    }
    valueChanged(newValue) {
        this.#setValueInForm(newValue);
        if (this.suggest) {
            this.#scheduleFilterProcessing();
        }
    }
    /**
     * The `input` event is emitted when a change to the element's value is
     * committed by the user.
     *
     * If `suggest = true`, this event is debounced by the `suggestDebounce` value.
     */
    input;
    /**
     * The `change` event is emitted when a change to the element's value is
     * committed by the user.
     *  - In normal mode (suggest = false), it is emitted after each input event.
     *
     *  - In suggest mode (suggest = true), it is emitted after the popover is closed
     * and a new value is committed by the user.
     *
     * This event is NOT debounced by the `suggestDebounce` value.
     */
    change;
    #findLargestValue = (model) => {
        this.#largestValue = findComboBoxLargestValue(model);
    };
    #getActualImagePathCallback = () => this.getImagePathCallback ??
        getControlRegisterProperty("getImagePathCallback", "ch-combo-box-render") ??
        DEFAULT_GET_IMAGE_PATH_CALLBACK;
    #setComboBoxIcons = () => {
        this.#itemImages = getComboBoxImages(this.model, this.#getActualImagePathCallback());
    };
    #scheduleFilterProcessing = () => {
        this.#applyFilters = true;
    };
    #filterModel = () => {
        // New filter value
        this.#displayedValues.clear();
        const filterOptions = {
            filter: this.value,
            options: this.suggestOptions
        };
        for (let index = 0; index < this.model.length; index++) {
            const item = this.model[index];
            filterSubModel(item, filterOptions, this.#displayedValues);
        }
        // Remove the active descendant if it is no longer rendered
        if (!this.#displayedValues.has(this.activeDescendant)) {
            this.activeDescendant = undefined;
        }
    };
    #updateFilters = () => {
        if (!this.suggest) {
            this.#displayedValues = undefined;
            return;
        }
        const modelIsAlreadyFiltered = this.#isModelAlreadyFiltered();
        // Check if the model already contains the filtered items
        if (!modelIsAlreadyFiltered) {
            this.#displayedValues ??= new Set();
            this.#filterModel();
        }
    };
    #getCurrentItemMapping = () => this.#captionToItemInfo.get(this.value) ??
        this.#valueToItemInfo.get(this.value);
    #checkAndEmitValueChangeWithNoFilter = () => {
        const activeDescendant = this.activeDescendant;
        // TODO: Should we debounce this event?
        if (activeDescendant?.value !== this.value) {
            // Clear last debounce
            clearTimeout(this.#queuedInputValueUpdate);
            this.value = this.suggest
                ? activeDescendant.caption ?? activeDescendant.value
                : activeDescendant.value;
            // Emit event
            this.input.emit(this.value);
            // Emit change event
            // TODO: Add a unit test for this
            this.#emitChangeEvent();
        }
    };
    #checkAndEmitValueChangeWithFilters = (event) => {
        if (!this.expanded) {
            return;
        }
        this.expanded = false;
        // The focus must return to the Host when tabbing with the popover
        // expanded
        this.#shouldFocusTheComboBox = true;
        event.preventDefault();
        // "Traditional selection". A value was selected pressing the enter key
        if (this.activeDescendant) {
            this.#checkAndEmitValueChangeWithNoFilter();
            return;
        }
        // No item was selected and the suggest is not strict
        if (!this.suggestOptions?.strict) {
            // TODO: Add a unit test: filters, no strict, value confirmation with
            // Enter or Tab. Expected: The value update should not be debounced
            // TODO: Avoid emitting duplicated input events if the value did not
            // changed
            // Clear last debounce and update the value right away, because the value
            // selection was confirmed
            clearTimeout(this.#queuedInputValueUpdate);
            this.value = this.#inputRef.value;
            this.input.emit(this.value);
            // TODO: Add a unit test for this
            this.#emitChangeEvent();
            return;
        }
        // Strict selection
        const inputValueMatches = this.#getCurrentItemMapping();
        if (inputValueMatches) {
            // TODO: Do we have to emit the change event?
            // TODO: Add a unit test for this
            this.#emitChangeEvent();
        }
        // Revert change because the input value does not match any item value
        else {
            // Clear last debounce
            clearTimeout(this.#queuedInputValueUpdate);
            this.value = this.#lastConfirmedValue;
            // Emit filter change event to recover the previous state.
            // TODO: Should we debounce this event?
            this.input.emit(this.value);
            // TODO: Add a unit test for this
            this.#emitChangeEvent();
        }
    };
    #handleSelectChange = (event) => {
        event.preventDefault();
        this.value = this.#selectRef.value;
        this.activeDescendant = this.#valueToItemInfo.get(this.activeDescendant.value)?.item;
        // Emit event
        this.input.emit(this.value);
        // TODO: Prevent change event in the native select
    };
    #handleExpandedChangeWithKeyBoard = (event) => {
        if (!this.suggest) {
            const keyboardHandler = this.#keyEventsNoFiltersDictionary[event.code];
            if (!keyboardHandler) {
                return;
            }
            keyboardHandler(event);
            if (!this.expanded) {
                this.#checkAndEmitValueChangeWithNoFilter();
            }
        }
        // Keyboard implementation for filters
        else {
            const keyboardHandler = this.#keyEventsWithFiltersDictionary[event.code];
            if (keyboardHandler) {
                keyboardHandler(event);
            }
        }
    };
    #handlePopoverClose = (event) => {
        event.stopPropagation();
        // The focus must return to the Host when the popover is closed using the
        // Escape key
        this.expanded = false;
        // TODO: When destroyItemsOnClose === true, StencilJS would throw 'The
        // "popoverClosed" event was emitted, but the dispatcher node is no longer
        // connected to the dom.', because the popoverOnClose event is emitted twice
        // in the ch-popover
        // Return the focus to the control if the popover was closed with the
        // escape key or by clicking again the combo-box. Don't return the focus if
        // the popover was closed because it is no longer visible, because it will
        // provoke a layout shift
        if (event.detail.reason !== "popover-no-longer-visible" &&
            focusComposedPath().includes(this.el)) {
            this.#shouldFocusTheComboBox = true;
        }
        if (this.suggest) {
            // Strict selection. Closing the popover should never confirm the current
            // value. In other words, it cancels the selection
            if (this.suggestOptions.strict) {
                // Clear last debounce
                clearTimeout(this.#queuedInputValueUpdate);
                // Revert change because the input value does not match any item value
                this.value = this.#lastConfirmedValue;
                // Emit filter change event to recover the previous state.
                // TODO: Should we debounce this event?
                this.input.emit(this.value);
            }
            // TODO: Add a unit test for this
            this.#emitChangeEvent();
        }
    };
    #emitChangeEvent = () => 
    // TODO: Add a unit test for this
    // TODO: Don't emit the event if the value didn't change
    this.change.emit(this.value);
    #handleInputFilterChange = (event) => {
        event.stopPropagation();
        this.expanded = true;
        // Clear last debounce
        clearTimeout(this.#queuedInputValueUpdate);
        // TODO: Add unit tests for this case
        if (this.suggestDebounce > 0) {
            this.#queuedInputValueUpdate = setTimeout(() => {
                this.value = this.#inputRef.value;
                this.input.emit(this.value);
            }, this.suggestDebounce);
        }
        else {
            this.value = this.#inputRef.value;
            this.input.emit(this.value);
        }
    };
    #displayPopover = (event) => {
        const clickWasPerformedInALabel = event.detail === 0;
        // TODO: Add a unit test for this case (clicking on the popover should not
        // close the popover)
        if (clickWasPerformedInALabel ||
            (this.expanded && popoverWasClicked(event))) {
            return;
        }
        event.stopPropagation();
        this.expanded = !this.expanded;
    };
    #handlePopoverClick = (event) => {
        const itemUIModel = getComboBoxItemFromMouseEvent(event, this.model);
        if (!itemUIModel) {
            return;
        }
        const itemGroup = itemUIModel;
        // Clicked in a group header
        if (itemGroup.items != null) {
            if (!itemGroup.expandable) {
                return;
            }
            // Toggle expanded
            itemGroup.expanded = !itemGroup.expanded;
        }
        // Clicked in a leaf
        else {
            this.activeDescendant = itemUIModel;
            this.expanded = false;
            this.#checkAndEmitValueChangeWithNoFilter();
        }
        forceUpdate(this);
    };
    #handleMouseOverItem = (event) => {
        const itemUIModel = getComboBoxItemFromMouseEvent(event, this.model);
        if (itemUIModel && itemUIModel.items == null) {
            this.activeDescendant = itemUIModel;
            forceUpdate(this);
        }
    };
    #isModelAlreadyFiltered = () => this.suggestOptions.alreadyProcessed === true;
    #shouldRenderActiveItemIcon = () => !this.suggest ||
        !this.expanded ||
        this.suggestOptions.renderActiveItemIconOnExpand;
    #setValueInForm = (value) => {
        // TODO: Add a unit test for this case
        if (!this.expanded) {
            this.#lastConfirmedValue = value;
        }
        this.#syncActiveDescendant();
        // Update form value
        this.internals.setFormValue(value);
    };
    #syncActiveDescendant = () => {
        // TODO: Should we set the activeDescendant at the end of this function?
        // If the active descendant is not set, try to set it using the value
        // TODO: Do we have to use the caption when using suggest?
        if (!this.activeDescendant || !this.expanded) {
            this.activeDescendant = this.#getCurrentItemMapping()?.item;
        }
        // If the value does not belong to a rendered item, remove the active
        // descendant
        const clientSideFilters = this.suggest && !this.#isModelAlreadyFiltered();
        if (!clientSideFilters &&
            !comboBoxActiveDescendantIsRendered(this.activeDescendant, this.model)) {
            this.activeDescendant = undefined;
        }
    };
    #nativeRender = () => (h("select", { "aria-label": this.#accessibleNameFromExternalLabel ?? this.accessibleName, disabled: this.disabled, onChange: !this.disabled && this.#handleSelectChange, ref: el => (this.#selectRef = el) }, !this.activeDescendant && (h("option", { disabled: true, selected: true, value: "" }, this.placeholder)), this.model.map(item => nativeItemRender(item, this.value))));
    connectedCallback() {
        this.#findLargestValue(this.model);
        mapValuesToItemInfo(this.model, this.#valueToItemInfo, this.#captionToItemInfo);
        // Accessibility
        this.#setValueInForm(this.value);
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-combo-box-render", labels, this.#accessibleNameFromExternalLabel, this.accessibleName);
    }
    componentWillRender() {
        if (this.#applyFilters) {
            this.#updateFilters(); // TODO: THERE IS A BUG IF THE COMBO-BOX STARTS WITH FILTERS APPLIED
            this.#applyFilters = false;
        }
    }
    componentDidRender() {
        if (this.expanded) {
            const selectedElement = this.el.shadowRoot.querySelector(SELECTED_ITEM_SELECTOR);
            // Focus the selected element to force the scroll into view
            if (selectedElement) {
                // Wait until the JS has been executed to avoid race conditions when
                // rendering elements in the top layer and trying to focus them
                requestAnimationFrame(() => {
                    selectedElement.focus();
                    this.#inputRef.focus();
                });
            }
        }
        if (this.#shouldFocusTheComboBox) {
            this.#shouldFocusTheComboBox = false;
            this.el.focus();
        }
    }
    render() {
        const filtersAreApplied = this.suggest;
        const disableTextSelection = !this.disabled && !filtersAreApplied;
        const comboBoxIsInteractive = !this.readonly && !this.disabled;
        const selectedItemInfo = this.#getCurrentItemMapping()?.item;
        const currentItemInInput = filtersAreApplied
            ? this.activeDescendant
            : selectedItemInfo;
        const computedImage = currentItemInInput?.startImgSrc && this.#shouldRenderActiveItemIcon()
            ? computeComboBoxItemImage(currentItemInInput, "start", this.#getActualImagePathCallback())
            : undefined;
        const startImgClasses = computedImage
            ? `img--start start-img-type--${currentItemInInput.startImgType ?? "background"} ${computedImage.classes}`
            : undefined;
        // TODO: UNIT TESTS.
        // - Clicking the combo-box with JS should not open the popover
        // - User click must open the combo-box
        // - Clicking the combo-box's label should not open the popover
        // TODO: Add unit tests for this feature.
        const currentValueMapping = selectedItemInfo?.value;
        const inputValue = filtersAreApplied
            ? this.value
            : selectedItemInfo?.caption;
        return (h(Host, { key: '213a3e1ffb84979d01898b0e650650b2a1860238', class: {
                "ch-disabled": this.disabled,
                "ch-combo-box--normal": !filtersAreApplied,
                "ch-combo-box--suggest": filtersAreApplied,
                "ch-combo-box--expanded": this.expanded
            },
            // TODO: Add unit tests for this feature, since it breaks custom parts
            // rendered outside of the ch-combo-box-render render() method
            part: tokenMap({
                [currentValueMapping]: !!currentValueMapping,
                // TODO: Add a unit test for this. Should we display the placeholder
                // even if a value is set without an option that is mapped to the
                // value?
                [COMBO_BOX_HOST_PARTS.PLACEHOLDER]: !inputValue,
                [this.hostParts]: !!this.hostParts
            }), onKeyDown: !mobileDevice &&
                comboBoxIsInteractive &&
                this.#handleExpandedChangeWithKeyBoard, onClickCapture: comboBoxIsInteractive &&
                (!filtersAreApplied || !this.expanded) &&
                this.#displayPopover }, mobileDevice
            ? this.#nativeRender()
            : [
                h("span", { class: "invisible-text" }, this.#largestValue || this.placeholder),
                h("div", { key: "combobox", role: "combobox", "aria-label": this.#accessibleNameFromExternalLabel ?? this.accessibleName, tabindex: disableTextSelection ? "0" : null, class: {
                        "input-container": true,
                        // TODO: Fix disabled styling when the group parent is disabled, but the option leaf isn't.
                        // Class for disabled images. Used when the combo-box or selected item are disabled
                        disabled: this.disabled || currentItemInInput?.disabled,
                        [startImgClasses]: !!startImgClasses
                    }, style: computedImage?.styles }, h("input", { "aria-controls": "popover", "aria-disabled": disableTextSelection ? "false" : null, "aria-expanded": this.expanded.toString(), "aria-haspopup": "true", autocomplete: "off", class: {
                        value: true,
                        "value--readonly": !filtersAreApplied
                    }, disabled: this.disabled || !filtersAreApplied, placeholder: this.placeholder, readOnly: this.readonly || !filtersAreApplied, value: inputValue, onInputCapture: filtersAreApplied &&
                        comboBoxIsInteractive &&
                        this.#handleInputFilterChange, ref: el => (this.#inputRef = el) })),
                this.expanded && comboBoxIsInteractive && (h("ch-popover", { key: "popover", id: "popover", role: "listbox", "aria-hidden": "false" // TODO: Remove this and add a unit test
                    ,
                    part: "window", actionById: true, actionElement: this.el, blockAlign: "outside-end", inlineAlign: this.popoverInlineAlign, closeOnClickOutside: true, show: true, popover: "manual", resizable: this.resizable, inlineSizeMatch: "action-element-as-minimum", overflowBehavior: "add-scroll", positionTry: "flip-block", onClick: this.#handlePopoverClick, onMouseOver: this.#handleMouseOverItem, onPopoverClosed: this.#handlePopoverClose }, this.model.map(customComboBoxItemRender(false, this.disabled, filtersAreApplied && !this.#isModelAlreadyFiltered(), this.activeDescendant, this.#displayedValues, this.#itemImages, ""))))
            ]));
    }
    static get is() { return "ch-combo-box-render"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["combo-box.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["combo-box.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ComboBoxImagePathCallback",
                    "resolved": "(item: ComboBoxItemModel, iconDirection: \"start\" | \"end\") => GxImageMultiState",
                    "references": {
                        "ComboBoxImagePathCallback": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/combo-box/types.ts::ComboBoxImagePathCallback"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nimgSrc needs to be resolved."
                }
            },
            "hostParts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in the Host element (`ch-combo-box-render`)."
                },
                "attribute": "host-parts",
                "reflect": false
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ComboBoxModel",
                    "resolved": "ComboBoxItemModel[]",
                    "references": {
                        "ComboBoxModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/combo-box/types.ts::ComboBoxModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the control"
                },
                "defaultValue": "[]"
            },
            "multiple": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that multiple options can be selected in the list.\nIf it is not specified, then only one option can be selected at a time.\nWhen multiple is specified, the control will show a scrolling list box\ninstead of a single line dropdown."
                },
                "attribute": "multiple",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the `name` of the control when used in a form."
                },
                "attribute": "name",
                "reflect": true
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A hint to the user of what can be entered in the control. Same as\n[placeholder](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-placeholder)\nattribute for `input` elements."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "popoverInlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the popover."
                },
                "attribute": "popover-inline-align",
                "reflect": false,
                "defaultValue": "\"inside-start\""
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "resizable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the control can be resized. If `true` the control can be\nresized at runtime by dragging the edges or corners."
                },
                "attribute": "resizable",
                "reflect": false,
                "defaultValue": "false"
            },
            "suggest": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you specify if the control behaves like a suggest.\nIf `true` the combo-box value will be editable an displayed items will be\nfiltered according to the input's value."
                },
                "attribute": "suggest",
                "reflect": false,
                "defaultValue": "false"
            },
            "suggestDebounce": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the debounce time (in ms) that the\ncontrol waits until it processes the changes to the filter property.\nConsecutive changes to the `value` property between this range, reset the\ntimeout to process the value.\nOnly works if `suggest === true`."
                },
                "attribute": "suggest-debounce",
                "reflect": false,
                "defaultValue": "250"
            },
            "suggestOptions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ComboBoxSuggestOptions",
                    "resolved": "{ alreadyProcessed?: boolean; autoExpand?: boolean; hideMatchesAndShowNonMatches?: boolean; highlightMatchedItems?: boolean; matchCase?: boolean; regularExpression?: boolean; renderActiveItemIconOnExpand?: boolean; strict?: boolean; }",
                    "references": {
                        "ComboBoxSuggestOptions": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/combo-box/types.ts::ComboBoxSuggestOptions"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you determine the options that will be applied to the\nsuggest."
                },
                "defaultValue": "{}"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the value (selected item) of the control."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "activeDescendant": {},
            "expanded": {}
        };
    }
    static get events() {
        return [{
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is emitted when a change to the element's value is\ncommitted by the user.\n\nIf `suggest = true`, this event is debounced by the `suggestDebounce` value."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "change",
                "name": "change",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `change` event is emitted when a change to the element's value is\ncommitted by the user.\n - In normal mode (suggest = false), it is emitted after each input event.\n\n - In suggest mode (suggest = true), it is emitted after the popover is closed\nand a new value is committed by the user.\n\nThis event is NOT debounced by the `suggestDebounce` value."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "expanded",
                "methodName": "handleExpandedChange"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "suggest",
                "methodName": "suggestChanged"
            }, {
                "propName": "suggestDebounce",
                "methodName": "suggestDebounceChanged"
            }, {
                "propName": "suggestOptions",
                "methodName": "suggestOptionsChanged"
            }, {
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
